import { useDispatchOnMount } from '../../../../hooks';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import { getGlobalScope, getProjectScope, getReleaseGroupScope } from '../../../../../shared/issuesV2';
import type { IssuesContainerParams } from '../../IssuesContainer';
import type { Scope } from '../../../../../shared/issuesV2';

type UseSetIssuesScopeParams =
  Omit<IssuesContainerParams, 'category' | 'reference' | 'reference_type'>
  & Partial<{ revisionScanId: number, releaseScanId: number}>;

/**
 * Returns the scope based on the URL params found.
 * Looks for project scope first, then release group scope,
 * before finally falling back to global scope.
 * Note that projectReleaseId comes from ReleaseGroupIssuesContainer
 * and not from the URL itself.
 */
const getScope = ({
  project_id: projectId,
  revision_id: revisionId,
  revisionScanId,
  releaseScanId,
  projectGroupId,
  projectReleaseId,
}: UseSetIssuesScopeParams): Scope => {
  if (projectId) {
    return getProjectScope(projectId, revisionId, revisionScanId);
  }
  if (projectGroupId) {
    return getReleaseGroupScope(projectGroupId, projectReleaseId as number, releaseScanId);
  }
  return getGlobalScope();
};

/**
 * Hook which reads the projectId and revisionId from the URL params (if present),
 * and sets the scope accordingly. This is called as soon as IssuesInbox/IssueDetails is mounted.
 *
 * @param {object} params - params object
 * @param {string} params.projectId - A given project ID, from the URL params.
 * @param {string} params.revisionId - A given revision ID, from the URL params.
 * @param {string} params.projectGroupId - A given release group ID, from the URL params.
 * @param {string} params.projectReleaseId - A given release ID, from the URL params.
 */
const useSetIssuesScope = (params: UseSetIssuesScopeParams): Scope => {
  const scope = getScope(params);
  useDispatchOnMount(IssuesV2Store.actions.setScope, scope);
  //  We return scope here so that it can be passed directly to `useIssuesSubnav`. See IssuesContainer for more info.
  return scope;
};

export default useSetIssuesScope;
